import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { environmentImmunities, filterEnvironmentalLayers, SNOW_GROUND_ID, ICE_GROUND_ID } from './environmental-layer-filter';
import type { EnvironmentImmunityReaders } from './environmental-layer-filter';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown,key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
const uuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const upper = (value: string): string => value.replace(/[a-z]/g,ch=>ch.toUpperCase());
const lower = (value: string): string => value.replace(/[A-Z]/g,ch=>ch.toLowerCase());
function clone<T>(value: T): T {
    if (!arrayLike(value)) return value;
    const entries = Object.entries(value);
    return (entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]))) as T;
}
const put = (row: Record<string,unknown>,key: string,value: unknown): void => {
    Object.defineProperty(row,key,{value,writable:true,enumerable:true,configurable:true});
};
function stringArgument(value: unknown): string {
    if (value == null || arrayLike(value)) throw new TypeError('Environment helper requires a scalar string');
    return phpJsonString(value);
}
const removed = new Set(['item_i','item_t','item_t_updated','item_private','item_deleted','item_created_by_id','item_updated_by_id',
    'item_id','item_name','item_type','item_description','flavor_text','source_id','source_link','preview','homebrew','item_price',
    'source_name','source_abbreviation','items','inventory']);
/** Actual clean_item_layers, including root-only picture-name filtering. The PHP
 * untyped helper warns on a scalar and returns it unchanged; callers own type gates.
 */
export function cleanEnvironmentalSource(input: unknown): unknown {
    if (!arrayLike(input)) return input;
    return clone(Object.fromEntries(Object.entries(input).filter(([key])=>!removed.has(key) && key !== 'source' && !lower(key).includes('picture'))));
}
function normalize(value: unknown): ItemFieldRow {
    if (value == null || value === '' || arrayLike(value) && Object.keys(value).length === 0) return [];
    return arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value,'0') ? value : [value];
}
function integerKey(key: string): boolean {
    if (!/^(?:0|-[1-9]\d*|[1-9]\d*)$/.test(key)) return false;
    const integer = BigInt(key); return integer >= -9223372036854775808n && integer <= 9223372036854775807n;
}
/** PHP array_merge, not recursive merge: numeric keys are renumbered and later
 * string keys replace earlier ones. The public result is the retained JSON shape.
 */
function merge(left: ItemFieldRow,right: ItemFieldRow): ItemFieldRow {
    const entries = new Map<string,unknown>(); let next = 0;
    for (const [key,value] of [...Object.entries(left),...Object.entries(right)]) entries.set(integerKey(key) ? String(next++) : key,value);
    return Object.fromEntries(entries);
}
function append(row: Record<string,unknown>,source: ItemFieldRow): void {
    const layers = own(source,'item_layer_types');
    if (arrayLike(layers)) {
        const old = own(row,'item_layer_types'), bucket = arrayLike(old) ? Object.fromEntries(Object.entries(old)) : {};
        put(row,'item_layer_types',bucket);
        for (const [key,raw] of Object.entries(layers)) {
            const entries = normalize(raw); if (Object.keys(entries).length === 0) continue;
            put(bucket,key,merge(normalize(own(bucket,key)),entries));
        }
    }
    const skip = new Set(['item_layer_types','computed','items','inventory','source','permissions','statuses']);
    for (const [key,raw] of Object.entries(source)) {
        if (skip.has(key)) continue;
        const entries = normalize(raw); if (Object.keys(entries).length === 0) continue;
        put(row,key,merge(normalize(own(row,key)),entries));
    }
}
export function appendEnvironmentalSource(input: ItemFieldRow,cleanedSource: ItemFieldRow): ItemFieldRow {
    if (!arrayLike(input) || !arrayLike(cleanedSource)) throw new TypeError('Environmental append requires PHP arrays');
    const row = Object.fromEntries(Object.entries(input)); append(row,cleanedSource); return clone(row);
}
/** Canonical nullable-string possessor_type_of, including a non-null wrapped
 * value preventing legacy fallback even when its first cell is null.
 */
export function readInventoryPossessorType(input: ItemFieldRow | null): string | null {
    if (input === null) return null;
    if (!arrayLike(input)) throw new TypeError('Possessor reader requires a PHP array or null');
    const value = own(own(own(input,'computed'),'possessor'),'type') ?? own(input,'possessor_type');
    const scalar = arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value,'0') ? own(value,'0') : value;
    return scalar == null ? null : stringArgument(scalar);
}
export interface InventoryEnvironmentMetadata { underwaterId: string; wetId: string }
/** Scalar context assembled by get_encounter_inventories_for_encounter_arr.
 * Preserve encounter_field's bucket precedence, null shadowing, spelling alias,
 * one-level wrapping and catalog-shell UUID rules.
 */
export function readInventoryFloorEnvironmentContext(encounter: ItemFieldRow): ItemFieldRow {
    if (!arrayLike(encounter)) throw new TypeError('Floor environment context requires a PHP array');
    function field(key: string): unknown {
        const keys = key === 'encounter_tempurature' ? [key,'encounter_temperature'] : [key];
        for (const candidate of keys) {
            const bucket = own(encounter,'encounters');
            const source = arrayLike(bucket) && Object.prototype.propertyIsEnumerable.call(bucket,candidate) ? bucket : encounter;
            if (!Object.prototype.propertyIsEnumerable.call(source,candidate)) continue;
            let value = own(source,candidate);
            if (arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value,'0')) value = own(value,'0');
            const id = own(own(value,'items'),'item_id');
            return typeof id === 'string' ? id : value;
        }
        return null;
    }
    return clone({possessor_type:'encounter',campaign_arr:own(encounter,'campaign_arr') ?? [],
        encounter_weather:field('encounter_weather') ?? '',encounter_environment:field('encounter_environment') ?? '',
        encounter_tempurature:field('encounter_tempurature') ?? '',encounter_wind:field('encounter_wind') ?? ''});
}

/** fetch_encounter_creatures joins these columns before the source builder's
 * ordered projection, including before generated presentation pointers. */
export function withEncounterCreatureEnvironment(source: ItemFieldRow, encounter?: ItemFieldRow | null): Record<string, unknown> {
    const row: Record<string, unknown> = { ...source };
    if (encounter && phpJsonTruthy(readItemField(source, 'encounter_id'))) {
        const axes = readInventoryFloorEnvironmentContext(encounter);
        for (const key of ['encounter_weather', 'encounter_environment', 'encounter_tempurature', 'encounter_wind'])
            row[key] = readItemField(axes, key);
    }
    return row;
}
export interface InventoryEnvironmentDependencies {
    metadata: InventoryEnvironmentMetadata;
    queryDefinition: (id: string,reason: 'encounter_weather' | 'merge_underwater_wet_onto_item_arr') => unknown;
    immunityReaders?: EnvironmentImmunityReaders;
}
// Floor objects use ground tags for snow/ice presentation and typed defenses when damaged.
// Creature rolls, movement, visibility and permanent statuses never apply to an object.
const OBJECT_ENVIRONMENT_LAYERS = new Set(['ground_terrain_type', 'damage_type_resistance',
    'damage_type_immunity', 'damage_type_vulnerability']);
function objectEnvironmentSource(source: ItemFieldRow): ItemFieldRow {
    const result = Object.fromEntries(Object.entries(source).filter(([key]) => OBJECT_ENVIRONMENT_LAYERS.has(key)));
    const layers = own(source, 'item_layer_types');
    if (arrayLike(layers)) result.item_layer_types = Object.fromEntries(Object.entries(layers)
        .filter(([key]) => OBJECT_ENVIRONMENT_LAYERS.has(key)));
    return result;
}
function weather(row: Record<string,unknown>,id: string,deps: InventoryEnvironmentDependencies, object = false): void {
    if (!uuid(id)) return;
    const definition = deps.queryDefinition(id,'encounter_weather');
    if (!phpJsonTruthy(definition)) return;
    const sourceLayers = cleanEnvironmentalSource(definition);
    if (!arrayLike(sourceLayers)) throw new TypeError('Cleaned weather requires a PHP array');
    let cleaned: ItemFieldRow = sourceLayers;
    const ec = own(row,'encounter_creatures');
    const currentWeather = readLayerListEntryUuid(own(ec,'encounter_weather') ?? '');
    const currentWind = readLayerListEntryUuid(own(ec,'encounter_wind') ?? '');
    // Native inventory-only hosts have no current weather and no active immunity.
    if (currentWeather !== '') {
        if (!deps.immunityReaders) throw new TypeError('Weather immunity requires host readers');
        const immunity = environmentImmunities(row,currentWeather,deps.immunityReaders);
        const isWeather = upper(id) === upper(currentWeather), isWind = upper(id) === upper(currentWind);
        const frozen = deps.immunityReaders.entries(definition as ItemFieldRow,'ground_terrain_type')
            .some(entry => [SNOW_GROUND_ID,ICE_GROUND_ID].includes(upper(readLayerListEntryUuid(entry))));
        const filtered = (source: ItemFieldRow): ItemFieldRow => filterEnvironmentalLayers(source as Record<string,unknown>,immunity,isWeather,isWind,frozen);
        cleaned = Object.fromEntries(Object.entries(cleaned));
        const layers = own(cleaned,'item_layer_types');
        if (arrayLike(layers)) (cleaned as Record<string,unknown>).item_layer_types = filtered(layers);
        cleaned = filtered(cleaned);
    }
    append(row,object ? objectEnvironmentSource(cleaned) : cleaned);
}
function underwater(row: Record<string,unknown>,id: string,deps: InventoryEnvironmentDependencies, object = false): void {
    if (!uuid(id) || deps.metadata.underwaterId === '' || upper(id) !== upper(deps.metadata.underwaterId) || !uuid(deps.metadata.wetId)) return;
    const definition = deps.queryDefinition(deps.metadata.wetId,'merge_underwater_wet_onto_item_arr');
    if (!phpJsonTruthy(definition)) return;
    const cleaned = cleanEnvironmentalSource(definition);
    if (!arrayLike(cleaned)) throw new TypeError('Cleaned wetness requires a PHP array');
    append(row,object ? objectEnvironmentSource(cleaned) : cleaned);
}
export function compileEncounterWeather(input: ItemFieldRow,rawId: string,deps: InventoryEnvironmentDependencies): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Weather compile requires a PHP array');
    const row = Object.fromEntries(Object.entries(input)); weather(row,stringArgument(rawId),deps); return clone(row);
}
export function compileUnderwaterWetness(input: ItemFieldRow,rawId: string,deps: InventoryEnvironmentDependencies): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Wetness compile requires a PHP array');
    const row = Object.fromEntries(Object.entries(input)); underwater(row,stringArgument(rawId),deps); return clone(row);
}
/** The actual build_inventory_arr environment block, in source order. Caller
 * context contains scalar axes; no nested encounter or persisted status is invented.
 */
export function compileInventoryEnvironment(input: ItemFieldRow,context: ItemFieldRow,deps: InventoryEnvironmentDependencies): ItemFieldRow {
    if (!arrayLike(input) || !arrayLike(context)) throw new TypeError('Inventory environment requires PHP arrays');
    const row = Object.fromEntries(Object.entries(input)), weatherId = phpJsonString(own(context,'encounter_weather') ?? '');
    if ((readInventoryPossessorType(row) ?? '') === 'encounter') {
        weather(row,weatherId,deps,true);
        weather(row,phpJsonString(own(context,'encounter_tempurature') ?? ''),deps,true);
        weather(row,phpJsonString(own(context,'encounter_wind') ?? ''),deps,true);
    }
    if ((readInventoryPossessorType(row) ?? '') === 'encounter') underwater(row,phpJsonString(own(context,'encounter_environment') ?? ''),deps,true);
    return clone(row);
}
